import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  REVIEWABLE_DOCUMENT_TYPES,
  ReviewableDocumentTypeKey,
  reviewableDocumentInputSchema,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ProofOfIdentityDocumentInput from '../../components/ProofOfIdentityDocumentInput/ProofOfIdentityDocumentInput';
import { extractReviewableDocumentInputFromNifOrder } from '../../utils/reviewable-document';
import { NifOrderStepPageProps } from '../types/stepProps';

const formSchema = z.object({
  proofOfIdentity: reviewableDocumentInputSchema,
});

type FormData = z.infer<typeof formSchema>;

function NifProofOfIdentityPage({
  nifOrder,
  previousUrl,
  navigateToNextStep,
}: NifOrderStepPageProps): JSX.Element {
  const defaultProofOfIdentity = extractReviewableDocumentInputFromNifOrder(
    nifOrder,
    'nif_proof_of_identity',
    (nifOrder.proofOfIdentityDocumentType?.key as ReviewableDocumentTypeKey) ??
      REVIEWABLE_DOCUMENT_TYPES.passport.key,
  );
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      proofOfIdentity: defaultProofOfIdentity,
    },
    resolver: zodResolver(formSchema),
  });
  const toast = useToast();

  const { t } = useTranslation(['nif']);

  function onSubmit(formData: FormData): Promise<void> {
    return updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: {
            intakeDocuments: [
              {
                documentCategoryId:
                  REVIEWABLE_DOCUMENT_CATEGORIES.nif_proof_of_identity.id,
                reviewableDocument: formData.proofOfIdentity,
              },
            ],
          },
        },
      },
    })
      .then((updatedOrder) => {
        navigateToNextStep(updatedOrder?.data?.updateNifOrder?.nifOrder);
      })
      .catch((err) => toast.error(logAndFormatError(err)));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{t('proof_of_identity.title', 'Proof of identification')}</h2>
        <p>
          {defaultProofOfIdentity.currentVersion?.documentTypeId ===
          REVIEWABLE_DOCUMENT_TYPES.passport.id
            ? t(
                'proof_of_identity.passport_instructions',
                "Upload a high-quality scan or photo of the applicant's passport.",
              )
            : t(
                'proof_of_identity.eu_id_instructions',
                "Upload a high-quality scan or photo of the applicant's EU National ID card.",
              )}
        </p>
      </div>
      <ProofOfIdentityDocumentInput control={control} name="proofOfIdentity" />
      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifProofOfIdentityPage;
